import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner,
} from "reactstrap"

// Toast notifications
import toastr from "toastr"
import "toastr/build/toastr.min.css"

// Formik Validation
import * as Yup from "yup"
import { useFormik } from "formik"

// action
import { registerUser, apiError } from "../../store/actions"

// Redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"

import { Link } from "react-router-dom"
import withRouter from "../../components/Common/withRouter"

// Login Redirect Utility
import { handleRegisterClose, getLoginRedirectPath, clearLoginRedirectPath } from "../../utils/loginRedirect"

// Social Login (Google OAuth)
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google"

// import images
import logo from "../../assets/images/logo-dark.png"
import logolight from "../../assets/images/logo-light.png"
import logodark from "../../assets/images/download1.png"
import googleIcon from "../../assets/images/Frame.png"
// import metaIcon from "../../assets/images/meta-logo.png" // Meta login temporarily disabled
import { useNavigate } from "react-router-dom";
import mainlogo from "../../assets/images/new_web_logo.png";
import { X } from 'lucide-react';

//Import config
import { google } from "../../config"
import { api} from "../../utils/variables"
import axios from "axios"

const Register = props => {
  document.title = " Register | Keyquest"

  const dispatch = useDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [activeTab, setActiveTab] = useState("signup")
  const [rememberMe, setRememberMe] = useState(false)
  const navigate = useNavigate();
  // const [showComingSoon, setShowComingSoon] = useState(false) // Meta login modal temporarily disabled

  //handleGoogleLoginResponse for @react-oauth/google
  const googleResponse = async (response) => {
    try {
      const idToken = response && (response.credential || response.tokenId)
      if (!idToken) {
        toastr.error("Invalid Google response", "Registration Error!")
        return
      }
      const res = await axios.post(`${api}/user/google-login`, { id_token: idToken })
      const accessToken = res?.data?.access_token
      if (!accessToken) {
        toastr.error("Failed to retrieve access token", "Registration Error!")
        return
      }
      try {
        const email = res?.data?.email || ""
        const userName = email ? (email.split("@")[0] || "") : ""
        localStorage.setItem("authToken", accessToken)
        if (email) localStorage.setItem("userEmail", email)
        if (userName) localStorage.setItem("userName", userName)
        localStorage.setItem("authUser", JSON.stringify(res?.data || {}))
      } catch (e) { }
      // Same convention as Login.js's Google handler: if this sign-up was
      // reached via an Upgrade/Unlock click (see navigateToRegisterForUpgrade
      // in loginRedirect.js), land straight on billing instead of /dashboard.
      // A normal sign-up never has anything stored here, so it's unaffected.
      const storedPath = getLoginRedirectPath()
      if (storedPath && storedPath !== '/') {
        clearLoginRedirectPath()
        props.router.navigate(storedPath)
      } else {
        props.router.navigate("/dashboard")
      }
    } catch (err) {
      const msg = err?.response?.data?.error || "Google registration failed"
      toastr.error(msg, "Registration Error!")
    }
  }


  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('userEmail')
    const rememberedState = localStorage.getItem('rememberMe') === 'true'

    if (rememberedEmail && rememberedState) {
      validation.setFieldValue('email', rememberedEmail)
      setRememberMe(true)
    }
  }, [])

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      rememberMe: false,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Please Enter Your Full Name"),
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string()
        .required("Please Enter Your Password")
        .matches(/^(?=.*[A-Z])/, "Password must contain at least one capital letter")
        .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
        .matches(/^(?=.*\d)/, "Password must contain at least one number")
        .min(8, "Password must be at least 8 characters long"),
      confirmPassword: Yup.string()
        .required("Please Confirm Your Password")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
    onSubmit: values => {
      dispatch({ type: "REGISTER_ERROR_CLEAR" })

      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
        localStorage.setItem('userEmail', values.email)
      } else {
        localStorage.removeItem('rememberMe')
        localStorage.removeItem('userEmail')
      }
      // Store several variants of the registered email so the verification
      // flow can reliably associate the activation with the correct user
      // even across different browsers or older client code.
      try { localStorage.setItem('pendingVerificationEmail', values.email) } catch (e) { }
      try { localStorage.setItem('registeredEmail', values.email) } catch (e) { }
      try { localStorage.setItem('firstAttemptEmail', values.email) } catch (e) { }

      dispatch(registerUser(values)).finally(() => {
        setRegisterLoading(false) // Stop loading
      })
    },
  })

  const selectAccountState = state => state.Account
  const AccountProperties = createSelector(selectAccountState, account => ({
    user: account.user,
    registrationError: account.registrationError,
    // loading: account.loading,
  }))

  const RegisterProperties = createSelector(selectAccountState, register => ({
    error: register.error,
  }))

  const { error } = useSelector(RegisterProperties)

  const {
    user,
    registrationError,
    // loading
  } = useSelector(AccountProperties)
  const [registerLoading, setRegisterLoading] = useState(false)
  // handleValidSubmit
  // const handleValidSubmit = (event, values) => {
  //   dispatch(registerUser(values));
  // }

  useEffect(() => {
    // Configure toastr options
    toastr.options = {
      positionClass: "toast-bottom-right",
      timeOut: 5000,
      extendedTimeOut: 1000,
      closeButton: true,
      progressBar: true,
      preventDuplicates: true,
      newestOnTop: true,
      showEasing: "swing",
      hideEasing: "linear",
      showMethod: "fadeIn",
      hideMethod: "fadeOut",
      showDuration: 1000,
      hideDuration: 1000
    }

    if (error) {
      setRegisterLoading(false)
      toastr.error(error, "Registration Error!")
    }
  }, [error])

  useEffect(() => {
    // Redirect to login page with success message when registration is successful
    if (user && user) {
      const message = 'Registration successful! Please check your email to verify your account.'
      // Don't set `firstLoginPrompt` here — only set it after the account is
      // verified (see `VerifyUser.js`). Setting it at registration causes the
      // chatbot to open for users who haven't activated their accounts.
      // Use the `navigate` hook and replace the history entry so the transient
      // registration state doesn't persist and re-trigger the toast later.
      try {
        navigate('/login', { state: { registrationSuccess: message }, replace: true })
        // Clear the transient registration user in the redux store so
        // revisiting the register page doesn't trigger another redirect.
        try { dispatch({ type: 'REGISTER_CLEAR_USER' }) } catch (e) { }
      } catch (e) {
        navigate('/login', { replace: true })
        try { dispatch({ type: 'REGISTER_CLEAR_USER' }) } catch (e) { }
      }
    }
  }, [user, navigate])

  useEffect(() => {
    if (registrationError) {
      toastr.error(registrationError, "Registration Error!")
    }
  }, [registrationError])

  useEffect(() => {
    dispatch(apiError(""))
    document.body.className = "authentication-bg"
    // remove classname when component will unmount
    return function cleanup() {
      document.body.className = ""
    }
  }, [dispatch])

  return (
    <React.Fragment>
      <div className="auth-container">
        <div className="auth-modal">
          {/* Close button */}
          <div className="close-button">
            <button
              onClick={() => handleRegisterClose(navigate)}
              className="close-icon"
              aria-label="Close register"
            >
              <X />
            </button>
          </div>

          <div className="auth-header">
            <div className="logo-container">
              <img src={mainlogo} alt="KeyQuest" className="logo-image" />
            </div>
            <h2 className="auth-title">Create Account</h2>
          </div>

          <p className="auth-subtitle">Join thousands of gamers tracking their progress</p>

          <div className="auth-tabs">
            <Link to="/login" className="tab-btn">
              Sign In
            </Link>
            <button
              className={`tab-btn ${activeTab === 'signup' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('signup')
                // Clear any transient navigation state (for example
                // registrationSuccess) to avoid re-triggering toasts when
                // toggling tabs.
                if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
                  try {
                    window.history.replaceState({}, document.title, window.location.pathname)
                  } catch (e) {
                    // ignore
                  }
                }
              }}
            >
              Sign Up
            </button>
          </div>

          <Form
            className="form-horizontal form-horizontal-login"
            onSubmit={e => {
              e.preventDefault()
              setRegisterLoading(true)

              // Validate the form manually
              validation.validateForm().then((errors) => {
                if (Object.keys(errors).length === 0) {
                  // Form is valid, proceed with submission
                  validation.handleSubmit()
                } else {
                  // Form has validation errors, reset loading state and show errors
                  setRegisterLoading(false)
                  // Set touched state to show errors
                  validation.setTouched({
                    fullName: true,
                    email: true,
                    password: true,
                    confirmPassword: true
                  })
                }
              })
              return false
            }}
          >
            {/* {registrationError && registrationError ? (
              <Alert color="danger">{registrationError}</Alert>
            ) : null} */}

            {/* {error ? <Alert color="danger">{error}</Alert> : <></>} */}

            <div className="form-group">
              <Label className="form-label">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                className={`form-input ${validation.touched.fullName && validation.errors.fullName ? 'is-invalid' : ''}`}
                placeholder="Enter your full name"
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.fullName || ""}
              />
              {validation.touched.fullName && validation.errors.fullName && (
                <FormFeedback type="invalid" className="error-message">
                  {validation.errors.fullName}
                </FormFeedback>
              )}
            </div>

            <div className="form-group">
              <Label className="form-label">Email Address</Label>
              <Input
                id="email"
                name="email"
                className={`form-input ${validation.touched.email && validation.errors.email ? 'is-invalid' : ''}`}
                placeholder="Enter your email"
                type="email"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}
              />
              {validation.touched.email && validation.errors.email && (
                <FormFeedback type="invalid" className="error-message">
                  {validation.errors.email}
                </FormFeedback>
              )}
            </div>



            <div className="form-group">
              <Label className="form-label">Password</Label>
              <div className="password-input-container">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className={`form-input ${validation.touched.password && validation.errors.password ? 'is-invalid' : ''}`}
                  placeholder="Enter your password"
                  onChange={(e) => {
                    validation.handleChange(e)
                  }}
                  onBlur={validation.handleBlur}
                  value={validation.values.password || ""}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {/* toggle icon same as before */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {showPassword ? (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </>
                    ) : (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </>
                    )}
                  </svg>
                </button>
              </div>

              {/* Password Rules - Only show when user is typing */}
              {validation.values.password && validation.values.password.length > 0 && (
                <div className="password-rules">
                  <p className={validation.values.password.match(/^(?=.*[A-Z])/) ? "valid" : "invalid"}>
                    {validation.values.password.match(/^(?=.*[A-Z])/) ? "✅" : "❌"} At least one uppercase letter
                  </p>
                  <p className={validation.values.password.match(/^(?=.*[a-z])/) ? "valid" : "invalid"}>
                    {validation.values.password.match(/^(?=.*[a-z])/) ? "✅" : "❌"} At least one lowercase letter
                  </p>
                  <p className={validation.values.password.match(/^(?=.*\d)/) ? "valid" : "invalid"}>
                    {validation.values.password.match(/^(?=.*\d)/) ? "✅" : "❌"} At least one number
                  </p>
                  <p className={validation.values.password.length >= 8 ? "valid" : "invalid"}>
                    {validation.values.password.length >= 8 ? "✅" : "❌"} Minimum 8 characters
                  </p>
                </div>
              )}

              {validation.touched.password && validation.errors.password && (
                <FormFeedback type="invalid" className="error-message">
                  {validation.errors.password}
                </FormFeedback>
              )}
            </div>


            <div className="form-group">
              <Label className="form-label">Confirm Password</Label>
              <div className="password-input-container">
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-input ${validation.touched.confirmPassword && validation.errors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Confirm your password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.confirmPassword || ""}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {showConfirmPassword ? (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </>
                    ) : (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                <FormFeedback type="invalid" className="error-message">
                  {validation.errors.confirmPassword}
                </FormFeedback>
              )}
            </div>

            <div className="form-options">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <Label className="form-check-label" for="remember-me">
                  Remember me
                </Label>
              </div>
              <Link to="/forgot-password" className="forgot-link">
                Forgot password?
              </Link>
            </div>

            <div className="form-submit">
              <button
                className="submit-btn"
                type="submit"
                disabled={registerLoading}
              >
                {registerLoading ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  "Sign up"
                )}
              </button>
            </div>

            <div className="divider">
              <span>Or continue with</span>
            </div>

            <div className="social-buttons">
              <GoogleOAuthProvider clientId={google.CLIENT_ID || ""}>
                <div className="google-button-wrapper">
                  <button type="button" className="social-btn google-btn">
                    <span className="google-icon">
                      <img src={googleIcon} alt="Google" />
                    </span>
                    <span className="google-text">
                      Google
                    </span>
                  </button>
                  <div className="google-overlay">
                    <GoogleLogin
                      onSuccess={googleResponse}
                      onError={() => { toastr.error("Google sign-in failed", "Registration Error!") }}
                      useOneTap={false}
                    />
                  </div>
                </div>
              </GoogleOAuthProvider>
              {/* Meta login button temporarily disabled
              <button
                type="button"
                className="social-btn meta-btn"
                onClick={() => {
                  setShowComingSoon(true)
                }}
              >
                <span className="meta-icon">
                  <img src={metaIcon} alt="Meta" />
                </span>
                <span className="meta-text">
                  Meta
                </span>
              </button>
              */}
            </div>

            <p className="terms-text">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="terms-link">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" className="terms-link">Privacy Policy</Link>.
            </p>
          </Form>
        </div>
      </div>

      {/* Coming Soon Modal for Meta login (temporarily disabled)
      <Modal isOpen={showComingSoon} toggle={() => setShowComingSoon(false)} centered className="coming-soon-modal">
        <ModalHeader toggle={() => setShowComingSoon(false)}>
          <span className="coming-soon-title">Coming Soon</span>
        </ModalHeader>
        <ModalBody>
          <div className="coming-soon-content">
            <div className="coming-soon-icon">
              <i className="mdi mdi-timer-sand"></i>
            </div>
            <p className="coming-soon-text">We’re working hard to enable this option. Please check back shortly.</p>
            <button type="button" className="coming-soon-btn" onClick={() => setShowComingSoon(false)}>Okay</button>
          </div>
        </ModalBody>
      </Modal>
      */}

      <style jsx>{`
        .form-horizontal-login .form-input {
          padding: 0 16px;
          height: 40px;
          border-radius: 8px;
          border: 1px solid #353748;
        }

        .form-horizontal-login .form-group {
          margin-bottom: 16px;
        }

        .form-horizontal-login .form-submit .submit-btn {
          padding: 10px 0;
          margin-bottom: 24px;
        }

        .auth-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .auth-modal {
          background: #21222d;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          width: 100%;
          max-width: 480px;
          position: relative;
          border: 1px solid #353748;
        }

        .close-button {
          position: absolute;
          top: 20px;
          right: 20px;
        }

        .close-icon {
          color: #a1acb8;
          font-size: 24px;
          text-decoration: none;
          line-height: 1;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-icon:hover {
          background: #3a3a3a;
          color: white;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 15px;
        }

        .logo-container {
          margin-bottom: 8px;
          text-align: center;
        }

        .logo-image {
          width: 185px;
          height: auto;
          object-fit: contain;
          padding-bottom: 15px;
        }

        .auth-title {
          color: #ffffff;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .auth-subtitle {
          color: #999;
          font-size: 16px;
          margin: 16px 0 24px 0;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-weight: 400;
        }

        .auth-tabs {
          display: flex;
          background: #1a1a1a;
          border-radius: 8px;
          padding: 4px;
          margin-bottom: 24px;
          width: 100%;
          border: 1px solid #353748;
        }

        .tab-btn {
          flex: 1;
          background: transparent;
          border: none;
          color: #87888c;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tab-btn:hover {
          color: #ccc;
          text-decoration: none;
        }

        .tab-btn.active {
          background: #f6255a;
          color: white;
          font-weight: 600;
          font-size: 16px;
        }

        .form-group {
          margin-bottom: 20px;
          width: 100%;
        }

        .form-label {
          color: white;
          margin-bottom: 8px;
          display: block;
          font-family: Rubik;
          font-weight: 500;
          font-size: 14px;
          line-height: 21px;
        }

        .form-input {
          background: #1a1a1a !important;
          border: 1px solid #333 !important;
          border-radius: 8px !important;
          color: white !important;
          padding: 12px 16px !important;
          font-size: 14px !important;
          width: 100% !important;
          transition: all 0.2s !important;
        }

        /* Force white text even when browser autofill applies its own styles */
        input.form-input:-webkit-autofill,
        input.form-input:-webkit-autofill:focus,
        input.form-input:-webkit-autofill:hover {
          -webkit-text-fill-color: #ffffff !important;
          -webkit-box-shadow: 0 0 0px 1000px #1a1a1a inset !important;
          box-shadow: 0 0 0px 1000px #1a1a1a inset !important;
        }

        input.form-input:-moz-autofill,
        input.form-input:-moz-autofill:focus {
          -moz-text-fill-color: #ffffff !important;
          box-shadow: 0 0 0px 1000px #1a1a1a inset !important;
        }

        .password-input,
        .form-input.password-input {
          color: #ffffff !important;
        }

        .form-input:focus {
          border: 1px solid #353748;
          box-shadow: 0 0 0 2px rgba(233, 30, 99, 0.2) !important;
          outline: none !important;
        }

        .form-input::placeholder {
          color: #666 !important;
        }

        .password-input-container {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #999;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .password-toggle:hover {
          color: #ccc;
        }

        .password-toggle svg {
          width: 20px;
          height: 20px;
        }

        .form-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          width: 100%;
          gap: 8px;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .form-check-input {
          width: 16px !important;
          height: 16px !important;
          background: #ffffff !important;
          border: 1px solid #666666 !important;
          border-radius: 2px !important;
          margin: 0 !important;
          flex-shrink: 0;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          position: relative;
        }

        .form-check-input:checked {
          background: #ff3366 !important;
          border-color: #ff3366 !important;
        }

        .form-check-input:checked::after {
          content: "✓";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 12px;
          font-weight: bold;
          line-height: 1;
        }

        .form-check-input:hover {
          border-color: #ff3366 !important;
        }

        .form-check-label {
          color: #e8e8e8 !important;
          font-size: 14px !important;
          font-weight: 400 !important;
          font-family: Rubik !important;
          margin: 0 !important;
          line-height: 21px !important;
          display: flex;
          align-items: center;
        }

        .forgot-link {
          color: #f6255a;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          display: none;
          align-items: center;
        }

        .forgot-link:hover {
          text-decoration: underline;
        }

        .submit-btn {
          width: 100%;
          background: #e91e63;
          border: none;
          color: white;
          padding: 14px 16px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 18px;
          min-width: 84px;
          max-width: 480px;
        }

        .submit-btn:hover {
          background: #f6255a;
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .divider {
          text-align: center;
          margin: 0 0 24px 0;
          position: relative;
          font-family: Rubik;
          font-weight: 400;
          font-size: 16px;
          line-height: 1;
        }

        .divider::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #333;
        }

        .divider span {
          padding: 0 16px;
          color: #ccc;
          font-size: 14px;
          position: relative;
          background: #21222d;
        }

        .social-buttons {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .social-btn {
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 20px;
          padding: 10px 16px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 188px;
          height: 40px;
          justify-content: center;
        }

        .social-btn:hover {
          background: #2a2a2a;
          border-color: #444;
        }

        .google-button-wrapper {
          position: relative;
          display: inline-block;
        }

        .google-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .google-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .google-text {
          font-family: Rubik;
          font-weight: 600;
          font-size: 16px;
          line-height: 1;
          color: #ffffff;
        }

        .google-overlay {
          position: absolute;
          inset: 0;
          opacity: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Meta login styles and modal (temporarily disabled)
        .meta-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .meta-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .meta-text {
          font-family: Rubik;
          font-weight: 600;
          font-size: 16px;
          line-height: 1;
          color: #ffffff;
        }

        .coming-soon-modal .modal-content {
          background: #21222d;
          border: 1px solid #353748;
          border-radius: 16px;
          color: #fff;
        }

        .coming-soon-modal .modal-header {
          border-bottom: 1px solid #353748;
          background: #21222d;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }

        .coming-soon-title {
          color: #ffffff;
          font-size: 18px;
          font-weight: 700;
        }

        .coming-soon-modal .modal-body {
          background: #21222d;
          padding: 24px;
        }

        .coming-soon-content {
          text-align: center;
        }

        .coming-soon-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 12px auto;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(246, 37, 90, 0.12);
          color: #f6255a;
          font-size: 28px;
        }

        .coming-soon-text {
          color: #e8e8e8;
          font-size: 14px;
          margin: 8px 0 16px 0;
          line-height: 1.5;
        }

        .coming-soon-btn {
          width: 100%;
          background: #e91e63;
          border: none;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .coming-soon-btn:hover {
          background: #f6255a;
        }
        */

        .terms-text {
          color: #e8e8e8;
          font-size: 12px;
          text-align: center;
          margin: 0;
          line-height: 1.5;
        }

        .terms-link {
          color: #e91e63;
          text-decoration: none;
        }

        .terms-link:hover {
          text-decoration: underline;
        }

        /* Error message styling */
        .error-message {
          color: #dc3545 !important;
          font-size: 12px !important;
          margin-top: 4px !important;
          display: block !important;
          font-family: Rubik !important;
          font-weight: 400 !important;
        }

        .form-input.is-invalid {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
        }

        .form-input.is-invalid:focus {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
        }

        .password-rules {
          margin-top: 8px;
          font-size: 12px;
          line-height: 1.5;
        }

        .password-rules p {
          margin: 2px 0;
          font-weight: 400;
        }

        .password-rules .valid {
          color: #28a745;
        }

        .password-rules .invalid {
          color: #dc3545;
        }

        @media (max-width: 480px) {
          .auth-container {
            padding: 16px;
          }

          .auth-modal {
            padding: 20px;
            margin: 0;
          }

          .auth-title {
            font-size: 24px;
          }

          .auth-subtitle {
            font-size: 14px;
          }

          .form-options {
            flex-direction: column;
            align-items: flex-start;
          }

          .social-buttons {
            flex-direction: column;
            align-items: stretch;
          }

          .social-btn {
            width: 100%;
            min-width: 0;
          }
        }

        /* Custom toast styling for dark theme */
        #toast-container > .toast {
          background-color: #21222d !important;
          color: #fff !important;
          border: 1px solid #353748 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
        }

        #toast-container > .toast-success {
          background-color: #21222d !important;
          border-color: #28a745 !important;
          border-left: 4px solid #28a745 !important;
        }

        #toast-container > .toast-error {
          background-color: #21222d !important;
          border-color: #dc3545 !important;
          border-left: 4px solid #dc3545 !important;
        }

        #toast-container > .toast-info {
          background-color: #21222d !important;
          border-color: #f6255a !important;
          border-left: 4px solid #f6255a !important;
        }

        #toast-container > .toast-warning {
          background-color: #21222d !important;
          border-color: #ffc107 !important;
          border-left: 4px solid #ffc107 !important;
          color: #212529 !important;
        }

        .toast-close-button {
          color: #fff !important;
          opacity: 0.8 !important;
        }

        .toast-close-button:hover {
          opacity: 1 !important;
        }

      `}</style>
    </React.Fragment>
  )
}
export default withRouter(Register)

Register.propTypes = {
  registerUser: PropTypes.func,
  // registerUserFailed: PropTypes.func,
  registrationError: PropTypes.any,
  user: PropTypes.any,
}
